import Personalizer from 'personalizer';
import { toParentChildFormat } from 'utils/ActivityTypesUtil';
import ActivityTypeMapper from 'utils/ActivityTypeMapper';
import { isUndefined, findWhere } from 'utils/nodash';
import { standardDeviation } from 'utils/ArrayUtils';
import {
    ColorLightuiAccent2,
    ColorBlueLight2,
    ColorLimeDark2,
    ColorOrangeLight3,
    ColorRedDark1,
    ColorRedDark3,
    ColorBlueDark3,
    ColorBluePrimary,
    ColorBlueLight1,
    ColorYellowPrimary,
    ColorOrangeDark1,
    Color408,
    Color201,
    Color700,
    Color412,
    Color301,
    ColorPurpleDark2,
    ColorOrangeLight1,
    ColorGreenLight1,
    ColorRedLight2,
    ColorDarkuiAccent3,
    ColorSecondary,
    ColorRedPrimary
} from 'ui-connect/styles/default-theme';
import TimeInZonesUtil from 'pages/activity_details_2/views/utils/TimeInZonesUtil';
import Garmin from 'garminmap';
import DateParser from 'utils/date/DateParser';
import ArrayUtil from 'utils/ArrayUtil';
import { getParentTypeDisplayOrderIndex, parentTypeDisplayOrder } from 'utils/ActivityTypesUtil';
import {
    WINDSURF_ACTIVE,
    RWD_STAND,
    RWD_WALK,
    RWD_RUN,
    OBSTACLE,
    ASCENT_SPLIT,
    DESCENT_SPLIT,
    TRANSITION,
    SKI_LIFT_SPLIT,
    SKI_RUN_SPLIT,
    SURFACE_TYPE_PAVED,
    SURFACE_TYPE_UNPAVED,
    DOWNHILL_BIKING_RUN_SPLIT,
    DOWNHILL_BIKING_LIFT_SPLIT
} from 'constants/TypedSplitsConstants';

const crossCountrySkiMetrics = [
    'distance',
    'duration',
    'elevationGain',
    'elevationLoss',
    'maxSpeed',
    'calories',
    'averageHR',
    'maxHR',
    'minTemperature',
    'maxTemperature',
    'averageSpeed',
    'avgPower'
];

const backCountrySkiMetrics = [
    'distance',
    'duration',
    'elevationGain',
    'elevationLoss',
    'maxSpeed',
    'calories',
    'averageHR',
    'maxHR',
    'minTemperature',
    'maxTemperature',
    'averageSpeed'
];

const resortSkiMetrics = [
    'distance',
    'duration',
    'elevationLoss',
    'maxSpeed', // ActivitySpeedModelUtil.js handles resort ski for this field properly
    'lapCount', // resort ski already handled in ActivityDataFieldsFormatter.getLapCount()
    'calories',
    'averageHR',
    'maxHR',
    'minTemperature',
    'maxTemperature',
    'averageSpeed' // ActivitySpeedModelUtil.js handles resort ski for this field properly
    // 'averageTemperature',
    // 'avgMovingSpeed' ??? TODO
];

/** all displayable metrics */
const surfingAllDisplayableMetrics = [
    'splitSummaries.noOfSplits.SURF_ACTIVE',
    'splitSummaries.maxDistance.SURF_ACTIVE',
    'splitSummaries.maxSpeed.SURF_ACTIVE',
    'splitSummaries.duration.SURF_ACTIVE',
    'duration',
    'splitSummaries.distance.SURF_ACTIVE',
    'splitSummaries.averageSpeed.SURF_ACTIVE',
    'calories',
    'maxHR',
    'averageHR'
];

export default {
    MAX_COMPARE_ITEMS: 4,
    numberOfSelectedItems: 0,
    unknownActivityTypeDefault: 'other',
    activityListColumnConfigDefaults: {
        running: ['distance', 'duration', 'averageSpeed', 'elevationGain', 'averageHR'],
        ultra_run: ['distance', 'duration', 'averageSpeed', 'elevationGain', 'averageHR'],
        obstacle_run: ['distance', 'duration', 'averageSpeed', 'elevationGain', 'averageHR'],
        cycling: ['distance', 'duration', 'averageSpeed', 'elevationGain', 'avgPower'],
        swimming: ['distance', 'duration', 'averageSpeed', 'calories', 'averageHR'],
        walking: ['distance', 'duration', 'averageSpeed', 'averageHR', 'calories'],
        hiking: ['distance', 'calories', 'duration', 'averageHR', 'averageSpeed'],
        [ActivityTypeMapper.Rucking]: ['distance', 'beginPackWeight', 'calories', 'duration', 'averageSpeed'],
        disc_golf: ['distance', 'calories', 'duration', 'averageHR', 'averageSpeed'],
        //TODO: Change 'totalSets' to 'activeSets' after API update
        fitness_equipment: ['totalSets', 'duration', 'averageHR', 'maxHR', 'calories'],
        [ActivityTypeMapper.Cardio]: ['totalSets', 'duration', 'averageHR', 'maxHR', 'calories'],
        [ActivityTypeMapper.Boxing]: ['totalSets', 'duration', 'averageHR', 'maxHR', 'calories'],
        [ActivityTypeMapper.MixedMartialArts]: ['totalSets', 'duration', 'averageHR', 'maxHR', 'calories'],
        other: ['distance', 'duration', 'averageSpeed', 'averageHR', 'calories'],
        water_sports: ['distance', 'duration', 'averageSpeed', 'averageHR', 'calories'],
        [ActivityTypeMapper.Kayaking]: ['distance', 'duration', 'averageSpeed', 'averageHR', 'calories'],
        [ActivityTypeMapper.Paddling]: ['distance', 'duration', 'averageSpeed', 'averageHR', 'calories'],
        [ActivityTypeMapper.StandUpPaddleboarding]: ['distance', 'duration', 'averageSpeed', 'averageHR', 'calories'],
        [ActivityTypeMapper.Rowing]: ['distance', 'duration', 'averageSpeed', 'averageHR', 'calories'],
        winter_sports: ['distance', 'duration', 'averageSpeed', 'averageHR', 'calories'],
        uncategorized: ['distance', 'calories', 'duration', 'averageHR', 'maxHR'],
        multi_sport: ['duration', 'distance', 'calories', 'elevationGain', 'averageSpeed'],
        indoor_climbing: [
            'splitSummaries.noOfSplits.CLIMB_ACTIVE',
            'splitSummaries.maxGradeValue.CLIMB_ACTIVE',
            'splitSummaries.duration.CLIMB_ACTIVE',
            'splitSummaries.totalAscent.CLIMB_ACTIVE',
            'splitSummaries.numClimbSends.CLIMB_ACTIVE'
        ],
        bouldering: [
            'splitSummaries.noOfSplits.CLIMB_ACTIVE',
            'splitSummaries.maxGradeValue.CLIMB_ACTIVE',
            'splitSummaries.duration.CLIMB_ACTIVE',
            'calories',
            'averageHR'
        ],
        apnea_diving: [
            'maxDepth',
            'bottomTime',
            'numberOfApneaDives',
            'totalSurfaceTime',
            'summarizedDiveInfo.waterType'
        ],
        pool_apnea: ['numberOfIntervals', 'longestInterval', 'sessionTime', 'averageHR', 'breathHoldTime'],
        apnea_hunting: [
            'maxDepth',
            'bottomTime',
            'numberOfApneaDives',
            'totalSurfaceTime',
            'summarizedDiveInfo.waterType'
        ],
        yoga: ['totalSets', 'duration', 'averageHR', 'maxHR', 'calories'],
        hiit: ['duration', 'averageHR', 'maxHR', 'totalSets', 'calories'],
        motorcycling: ['distance', 'duration', 'averageSpeed', 'averageHR', 'calories'],
        diving: ['maxDepth', 'bottomTime', 'minTemperature', 'calories', 'surfaceInterval'],
        auto_racing: ['bestLapTime', 'maxSpeed', 'lapCount', 'elevationGain', 'elevationLoss'],
        safety: ['duration', 'averageHR', 'distance', 'averageSpeed', 'maxSpeed'],
        resort_skiing_snowboarding_ws: ['distance', 'duration', 'elevationLoss', 'maxSpeed', 'lapCount'],
        resort_skiing: ['distance', 'duration', 'elevationLoss', 'maxSpeed', 'lapCount'],
        resort_snowboarding: ['distance', 'duration', 'elevationLoss', 'maxSpeed', 'lapCount'],
        backcountry_skiing_snowboarding_ws: ['distance', 'duration', 'elevationGain', 'elevationLoss', 'averageHR'],
        backcountry_skiing: ['distance', 'duration', 'elevationGain', 'elevationLoss', 'averageHR'],
        backcountry_snowboarding: ['distance', 'duration', 'elevationGain', 'elevationLoss', 'averageHR'],
        cross_country_skiing_ws: ['distance', 'duration', 'averageSpeed', 'avgPower', 'averageHR'],
        //Any Changes to cross_country_skiing array should be made to Skate Ski
        skate_skiing_ws: ['distance', 'duration', 'averageSpeed', 'avgPower', 'averageHR'],
        breathwork: ['duration', 'averageHR', 'maxHR', 'avgRespirationRate', 'differenceStress'],
        meditation: ['duration', 'averageHR', 'maxHR', 'avgRespirationRate', 'differenceStress'],
        [ActivityTypeMapper.OffShoreGrinding]: [
            'maxSpeed',
            'duration',
            'averageHR',
            'avgPower',
            'averageBikingCadenceInRevPerMinute'
        ],
        [ActivityTypeMapper.OffShoreGrindingV2]: [
            'maxSpeed',
            'duration',
            'averageHR',
            'avgPower',
            'averageBikingCadenceInRevPerMinute'
        ],
        [ActivityTypeMapper.OnShoreGrinding]: [
            'duration',
            'averageHR',
            'avgPower',
            'averageBikingCadenceInRevPerMinute',
            'calories'
        ],
        [ActivityTypeMapper.OnShoreGrindingV2]: [
            'duration',
            'averageHR',
            'avgPower',
            'averageBikingCadenceInRevPerMinute',
            'calories'
        ],
        [ActivityTypeMapper.Surfing]: [
            'splitSummaries.noOfSplits.SURF_ACTIVE',
            'splitSummaries.maxDistance.SURF_ACTIVE',
            'splitSummaries.maxSpeed.SURF_ACTIVE',
            'splitSummaries.duration.SURF_ACTIVE',
            'duration'
        ],
        [ActivityTypeMapper.SurfingV2]: [
            'splitSummaries.noOfSplits.SURF_ACTIVE',
            'splitSummaries.maxDistance.SURF_ACTIVE',
            'splitSummaries.maxSpeed.SURF_ACTIVE',
            'splitSummaries.duration.SURF_ACTIVE',
            'sessionTime'
        ],
        water_tubing: [
            // wakesports
            'splitSummaries.noOfSplits.SURF_ACTIVE',
            'splitSummaries.maxDistance.SURF_ACTIVE',
            'splitSummaries.maxSpeed.SURF_ACTIVE',
            'splitSummaries.duration.SURF_ACTIVE',
            'duration'
        ],
        [ActivityTypeMapper.Wakeboarding]: [
            // wakesports
            'splitSummaries.noOfSplits.SURF_ACTIVE',
            'splitSummaries.maxDistance.SURF_ACTIVE',
            'splitSummaries.maxSpeed.SURF_ACTIVE',
            'splitSummaries.duration.SURF_ACTIVE',
            'duration'
        ],
        [ActivityTypeMapper.WakeboardingV2]: [
            // wakesports
            'splitSummaries.noOfSplits.SURF_ACTIVE',
            'splitSummaries.maxDistance.SURF_ACTIVE',
            'splitSummaries.maxSpeed.SURF_ACTIVE',
            'splitSummaries.duration.SURF_ACTIVE',
            'duration'
        ],
        wakesurfing: [
            // wakesports
            'splitSummaries.noOfSplits.SURF_ACTIVE',
            'splitSummaries.maxDistance.SURF_ACTIVE',
            'splitSummaries.maxSpeed.SURF_ACTIVE',
            'splitSummaries.duration.SURF_ACTIVE',
            'duration'
        ],
        waterskiing: [
            // wakesports
            'splitSummaries.noOfSplits.SURF_ACTIVE',
            'splitSummaries.maxDistance.SURF_ACTIVE',
            'splitSummaries.maxSpeed.SURF_ACTIVE',
            'splitSummaries.duration.SURF_ACTIVE',
            'duration'
        ],
        [ActivityTypeMapper.Esport]: ['duration', 'avgStress', 'averageHR', 'maxStress'],
        [ActivityTypeMapper.Windsurfing]: ['distance', 'duration', 'averageSpeed', 'maxSpeed', 'averageHR'],
        [ActivityTypeMapper.WindsurfingV2]: ['distance', 'duration', 'averageSpeed', 'maxSpeed', 'averageHR'],
        [ActivityTypeMapper.Snorkeling]: ['duration', 'averageHR', 'calories', 'distance'],
        wheelchair_push_run: ['distance', 'duration', 'averageSpeed', 'elevationGain', 'averageHR'],
        wheelchair_push_walk: ['distance', 'duration', 'averageSpeed', 'averageHR', 'calories'],
        para_sports: ['distance', 'duration', 'averageSpeed', 'elevationGain', 'averageHR'],
        [ActivityTypeMapper.TeamSports]: ['distance', 'duration', 'averageSpeed', 'averageHR', 'calories'],
        [ActivityTypeMapper.RacketSports]: ['distance', 'duration', 'averageSpeed', 'averageHR', 'calories'],
        [ActivityTypeMapper.JumpRope]: ['totalSets', 'duration', 'averageHR', 'maxHR', 'calories'],
        [ActivityTypeMapper.Shooting]: ['duration', 'averageHR', 'avgStress', 'avgRespirationRate', 'calories']
    },

    dummyMetricValues: {
        distance: '--',
        duration: '--',
        averageSpeed: '--',
        maxSpeed: '--',
        elevationGain: '--',
        elevationLoss: '--',
        averageHR: '--',
        maxHR: '--',
        avgPower: '--',
        maxPower: '--',
        calories: '--',
        averageBikingCadenceInRevPerMinute: '--',
        maxBikingCadenceInRevPerMinute: '--',
        averageRunningCadenceInStepsPerMinute: '--',
        maxRunningCadenceInStepsPerMinute: '--',
        averageSwimCadenceInStrokesPerMinute: '--',
        avgStrideLength: '--',
        avgVerticalRatio: '--',
        avgVerticalOscillation: '--',
        avgStepSpeedLoss: '--',
        avgStepSpeedLossPercent: '--',
        avgGroundContactTime: '--',
        avgGroundContactBalance: '--',
        normPower: '--',
        leftBalance: '--',
        trainingStressScore: '--',
        max20MinPower: '--',
        strokes: '--',
        averageSwolf: '--',
        aerobicTrainingEffect: '--',
        maxDepth: '--',
        minTemperature: '--',
        maxTemperature: '--',
        bottomTime: '--',
        isDecoDive: '--',
        gasType: '--',
        visibility: '--',
        grit: '--',
        flow: '--',
        surfaceInterval: '--',
        'summarizedDiveInfo.weight': '--',
        'summarizedDiveInfo.current': '--',
        'summarizedDiveInfo.surfaceCondition': '--',
        'summarizedDiveInfo.waterType': '--',
        'splitSummaries.maxGradeValue.CLIMB_ACTIVE': '--',
        'splitSummaries.noOfSplits.CLIMB_ACTIVE': '--',
        'splitSummaries.duration.CLIMB_ACTIVE': '--',
        'splitSummaries.totalAscent.CLIMB_ACTIVE': '--',
        'splitSummaries.numClimbSends.CLIMB_ACTIVE': '--',
        'splitSummaries.duration.CLIMB_REST': '--',
        'splitSummaries.maxElevationGain.CLIMB_ACTIVE': '--',
        'splitSummaries.numClimbsAttempted.CLIMB_ACTIVE': '--',
        'splitSummaries.numClimbsCompleted.CLIMB_ACTIVE': '--',
        'splitSummaries.numFalls.CLIMB_ACTIVE': '--',
        'splitSummaries.duration.RUN_REST': '--',
        //TODO: Change 'totalSets' to 'activeSets' after API update
        totalSets: '--',
        totalReps: '--',
        lapCount: '--',
        bestLapTime: '--',
        avgRespirationRate: '--',
        minRespirationRate: '--',
        maxRespirationRate: '--',
        differenceStress: '--',
        beginStress: '--',
        endStress: '--',
        avgStress: '--',
        maxStress: '--',
        'splitSummaries.distance.SURF_ACTIVE': '--',
        'splitSummaries.noOfSplits.SURF_ACTIVE': '--',
        'splitSummaries.maxDistance.SURF_ACTIVE': '--',
        'splitSummaries.maxSpeed.SURF_ACTIVE': '--',
        'splitSummaries.duration.SURF_ACTIVE': '--',
        'splitSummaries.averageSpeed.SURF_ACTIVE': '--',
        elapsedDuration: '--',
        movingDuration: '--',
        minElevation: '--',
        maxElevation: '--',
        avgGradeAdjustedSpeed: '--',
        avgWheelchairCadence: '--',
        maxWheelchairCadence: '--',
        resortSkiDistance: '--',
        resortSkiElevationLoss: '--',
        beginPackWeight: '--',
        numberOfIntervals: '--',
        longestInterval: '--',
        sessionTime: '--',
        breathHoldTime: '--',
        downhillBikingDistance: '--',
        downhillBikingElevationLoss: '--',
        steps: '--'
    },

    activityListAllDisplayableMetrics: {
        running: [
            'distance',
            'calories',
            'duration',
            'averageHR',
            'maxHR',
            'aerobicTrainingEffect',
            // 'anaerobicTrainingEffect',  TODO ??
            'averageRunningCadenceInStepsPerMinute',
            'maxRunningCadenceInStepsPerMinute',
            'averageSpeed',
            'maxSpeed',
            'elevationGain',
            'elevationLoss',
            // 'avgMovingPace', ??? TODO
            'avgStrideLength',
            'avgVerticalRatio',
            'avgVerticalOscillation',
            'avgStepSpeedLoss',
            'avgStepSpeedLossPercent',
            'avgGroundContactTime',
            'avgGroundContactBalance',
            'avgGradeAdjustedSpeed',
            'beginPackWeight'
        ],
        ultra_run: [
            'distance',
            'calories',
            'duration',
            'splitSummaries.duration.RUN_REST',
            'averageHR',
            'maxHR',
            'aerobicTrainingEffect',
            // 'anaerobicTrainingEffect',  TODO ??
            'averageRunningCadenceInStepsPerMinute',
            'maxRunningCadenceInStepsPerMinute',
            'averageSpeed',
            'maxSpeed',
            'elevationGain',
            'elevationLoss',
            // 'avgMovingPace', ??? TODO
            'avgStrideLength',
            'avgVerticalRatio',
            'avgVerticalOscillation',
            'avgGroundContactTime',
            'avgGroundContactBalance',
            'avgGradeAdjustedSpeed',
            'beginPackWeight'
        ],
        obstacle_run: [
            'distance',
            'calories',
            'duration',
            'averageHR',
            'maxHR',
            'averageRunningCadenceInStepsPerMinute',
            'maxRunningCadenceInStepsPerMinute',
            'averageSpeed',
            'maxSpeed',
            'elevationGain',
            'elevationLoss',
            'avgMovingPace',
            'avgStrideLength',
            'avgVerticalRatio',
            'avgVerticalOscillation',
            'avgGroundContactTime',
            'avgGroundContactBalance',
            'avgGradeAdjustedSpeed',
            'beginPackWeight'
        ],
        cycling: [
            'distance',
            'calories',
            'duration',
            'averageHR',
            'maxHR',
            'aerobicTrainingEffect',
            // 'anaerobicTrainingEffect',  TODO ??
            'averageBikingCadenceInRevPerMinute',
            'maxBikingCadenceInRevPerMinute',
            'averageSpeed',
            'maxSpeed',
            'normPower',
            'leftBalance',
            'trainingStressScore',
            'max20MinPower',
            'avgPower',
            'maxPower',
            'elevationGain',
            'elevationLoss',
            'grit',
            'flow'
            // 'avgMovingSpeed', ??? TODO
        ],
        swimming: [
            'distance',
            'calories',
            'duration',
            'averageHR',
            'maxHR',
            'strokes',
            'averageSwolf',
            'averageSwimCadenceInStrokesPerMinute',
            // 'maxSwimCadenceInStrokesPerMinute' // TODO - not sure if min or max
            'averageSpeed'
            // 'avgMovingPace', ??? TODO
        ],
        walking: [
            'distance',
            'calories',
            'duration',
            'averageHR',
            'maxHR',
            'averageRunningCadenceInStepsPerMinute',
            'maxRunningCadenceInStepsPerMinute',
            'averageSpeed',
            'maxSpeed',
            'elevationGain',
            'elevationLoss',
            // 'avgMovingPace', ??? TODO
            'avgStrideLength',
            'avgGradeAdjustedSpeed',
            'beginPackWeight',
            'steps'
        ],
        hiking: [
            'distance',
            'calories',
            'duration',
            'averageHR',
            'maxHR',
            'averageRunningCadenceInStepsPerMinute',
            'maxRunningCadenceInStepsPerMinute',
            'averageSpeed',
            'maxSpeed',
            'elevationGain',
            'elevationLoss',
            // 'avgMovingPace', ??? TODO
            'avgStrideLength',
            'avgGradeAdjustedSpeed',
            'beginPackWeight',
            'steps'
        ],
        [ActivityTypeMapper.Rucking]: [
            'distance',
            'calories',
            'duration',
            'averageHR',
            'maxHR',
            'averageRunningCadenceInStepsPerMinute',
            'maxRunningCadenceInStepsPerMinute',
            'averageSpeed',
            'maxSpeed',
            'elevationGain',
            'elevationLoss',
            'avgStrideLength',
            'avgGradeAdjustedSpeed',
            'beginPackWeight',
            'steps'
        ],
        disc_golf: [
            'distance',
            'calories',
            'duration',
            'averageHR',
            'maxHR',
            'averageRunningCadenceInStepsPerMinute',
            'maxRunningCadenceInStepsPerMinute',
            'averageSpeed',
            'maxSpeed',
            'elevationGain',
            'elevationLoss',
            // 'avgMovingPace', ??? TODO
            'avgStrideLength',
            'avgGradeAdjustedSpeed'
        ],
        fitness_equipment: [
            'distance',
            'calories',
            'duration',
            'averageHR',
            'maxHR',
            'totalReps',
            //TODO: Change 'totalSets' to 'activeSets' after API update
            'totalSets'
            // 'totalVolume',
            // 'workTime',
            // 'restTime'
            // 'avgMovingPace', ??? TODO
        ],
        [ActivityTypeMapper.Cardio]: [
            'distance',
            'calories',
            'duration',
            'averageHR',
            'maxHR',
            'totalReps',
            //TODO: Change 'totalSets' to 'activeSets' after API update
            'totalSets'
        ],
        [ActivityTypeMapper.Boxing]: [
            'distance',
            'calories',
            'duration',
            'averageHR',
            'maxHR',
            'totalReps',
            //TODO: Change 'totalSets' to 'activeSets' after API update
            'totalSets'
        ],
        [ActivityTypeMapper.MixedMartialArts]: [
            'distance',
            'calories',
            'duration',
            'averageHR',
            'maxHR',
            'totalReps',
            //TODO: Change 'totalSets' to 'activeSets' after API update
            'totalSets'
        ],
        indoor_climbing: [
            'splitSummaries.noOfSplits.CLIMB_ACTIVE',
            'splitSummaries.maxGradeValue.CLIMB_ACTIVE',
            'splitSummaries.duration.CLIMB_ACTIVE',
            'splitSummaries.maxElevationGain.CLIMB_ACTIVE',
            'splitSummaries.numClimbSends.CLIMB_ACTIVE',
            'splitSummaries.totalAscent.CLIMB_ACTIVE',
            'splitSummaries.numClimbsAttempted.CLIMB_ACTIVE',
            'splitSummaries.numClimbsCompleted.CLIMB_ACTIVE',
            'splitSummaries.numFalls.CLIMB_ACTIVE',
            'calories',
            'averageHR',
            'maxHR',
            'splitSummaries.duration.CLIMB_REST',
            'duration'
        ],
        bouldering: [
            'splitSummaries.noOfSplits.CLIMB_ACTIVE',
            'splitSummaries.maxGradeValue.CLIMB_ACTIVE',
            'splitSummaries.duration.CLIMB_ACTIVE',
            'splitSummaries.numClimbsAttempted.CLIMB_ACTIVE',
            'splitSummaries.numClimbsCompleted.CLIMB_ACTIVE',
            'calories',
            'averageHR',
            'maxHR',
            'splitSummaries.duration.CLIMB_REST',
            'duration'
        ],
        yoga: ['distance', 'calories', 'duration', 'averageHR', 'maxHR', 'totalSets', 'totalReps'],
        hiit: ['duration', 'averageHR', 'maxHR', 'totalSets', 'calories'],
        other: ['distance', 'calories', 'duration', 'averageHR', 'maxHR', 'averageSpeed'],
        water_sports: ['distance', 'calories', 'duration', 'averageHR', 'maxHR', 'averageSpeed'],
        winter_sports: ['distance', 'calories', 'duration', 'averageHR', 'maxHR', 'averageSpeed'],
        uncategorized: ['distance', 'calories', 'duration', 'averageHR', 'maxHR', 'averageSpeed'],
        multi_sport: ['duration', 'distance', 'calories', 'elevationGain', 'averageSpeed'],
        motorcycling: ['distance', 'calories', 'duration', 'averageHR', 'maxHR', 'averageSpeed'],
        diving: [
            'maxDepth',
            'bottomTime',
            'minTemperature',
            'gasType',
            'surfaceInterval',
            'isDecoDive',
            //'visibility', //todo uncomment this when api finished visibility
            'calories',
            'summarizedDiveInfo.weight',
            'summarizedDiveInfo.current',
            'summarizedDiveInfo.surfaceCondition',
            'summarizedDiveInfo.waterType'
        ],
        safety: [
            'duration',
            'averageHR',
            'distance',
            'averageSpeed',
            'maxSpeed',
            'elevationGain',
            'elevationLoss',
            'calories',
            'maxHR'
        ],
        auto_racing: [
            'bestLapTime',
            'maxSpeed',
            'lapCount',
            'elevationGain',
            'elevationLoss',
            'minTemperature',
            'maxTemperature',
            'averageSpeed',
            'distance',
            'duration',
            'calories',
            'averageHR',
            'maxHR'
        ],
        resort_skiing_snowboarding_ws: resortSkiMetrics,
        resort_skiing: resortSkiMetrics,
        resort_snowboarding: resortSkiMetrics,
        backcountry_skiing_snowboarding_ws: backCountrySkiMetrics,
        backcountry_skiing: backCountrySkiMetrics,
        backcountry_snowboarding: backCountrySkiMetrics,
        cross_country_skiing_ws: crossCountrySkiMetrics,
        // skate ski same as cross country ski
        skate_skiing_ws: crossCountrySkiMetrics,
        breathwork: [
            'duration',
            'averageHR',
            'maxHR',
            'avgRespirationRate',
            'minRespirationRate',
            'maxRespirationRate',
            'differenceStress',
            'beginStress',
            'endStress'
        ],
        meditation: [
            'duration',
            'averageHR',
            'maxHR',
            'avgRespirationRate',
            'minRespirationRate',
            'maxRespirationRate',
            'differenceStress',
            'beginStress',
            'endStress'
        ],
        [ActivityTypeMapper.OffShoreGrinding]: [
            'averageSpeed',
            'maxSpeed',
            'duration',
            'averageHR',
            'maxHR',
            'avgPower',
            'maxPower',
            'averageBikingCadenceInRevPerMinute',
            'maxBikingCadenceInRevPerMinute',
            'calories',
            'distance'
        ],
        [ActivityTypeMapper.OffShoreGrindingV2]: [
            'averageSpeed',
            'maxSpeed',
            'duration',
            'averageHR',
            'maxHR',
            'avgPower',
            'maxPower',
            'averageBikingCadenceInRevPerMinute',
            'maxBikingCadenceInRevPerMinute',
            'calories',
            'distance'
        ],
        [ActivityTypeMapper.OnShoreGrinding]: [
            'duration',
            'averageHR',
            'maxHR',
            'avgPower',
            'maxPower',
            'averageBikingCadenceInRevPerMinute',
            'maxBikingCadenceInRevPerMinute',
            'calories',
            'distance'
        ],
        [ActivityTypeMapper.OnShoreGrindingV2]: [
            'duration',
            'averageHR',
            'maxHR',
            'avgPower',
            'maxPower',
            'averageBikingCadenceInRevPerMinute',
            'maxBikingCadenceInRevPerMinute',
            'calories',
            'distance'
        ],
        [ActivityTypeMapper.Surfing]: surfingAllDisplayableMetrics,
        [ActivityTypeMapper.SurfingV2]: surfingAllDisplayableMetrics,

        // water sports
        [ActivityTypeMapper.Watertubing]: surfingAllDisplayableMetrics,
        [ActivityTypeMapper.Wakeboarding]: surfingAllDisplayableMetrics,
        [ActivityTypeMapper.WakeboardingV2]: surfingAllDisplayableMetrics,
        [ActivityTypeMapper.Wakesurfing]: surfingAllDisplayableMetrics,
        [ActivityTypeMapper.Waterskiing]: surfingAllDisplayableMetrics,

        [ActivityTypeMapper.Esport]: ['duration', 'avgStress', 'averageHR', 'maxStress'],
        [ActivityTypeMapper.Windsurfing]: [
            'distance',
            'averageSpeed',
            'maxSpeed',
            'duration',
            'movingDuration',
            'elapsedDuration',
            'averageHR',
            'maxHR',
            'elevationGain',
            'elevationLoss',
            'minElevation',
            'maxElevation',
            'calories'
        ],
        [ActivityTypeMapper.WindsurfingV2s]: [
            'distance',
            'averageSpeed',
            'maxSpeed',
            'duration',
            'movingDuration',
            'elapsedDuration',
            'averageHR',
            'maxHR',
            'elevationGain',
            'elevationLoss',
            'minElevation',
            'maxElevation',
            'calories'
        ],
        [ActivityTypeMapper.Snorkeling]: ['distance', 'calories', 'duration', 'averageHR', 'maxHR', 'averageSpeed'],
        wheelchair_push_run: [
            'distance',
            'duration',
            'averageSpeed',
            'elevationGain',
            'averageHR',
            'avgWheelchairCadence',
            'maxWheelchairCadence',
            'strokes'
        ],
        wheelchair_push_walk: [
            'distance',
            'duration',
            'averageSpeed',
            'averageHR',
            'calories',
            'avgWheelchairCadence',
            'maxWheelchairCadence',
            'strokes'
        ],
        para_sports: [
            'distance',
            'duration',
            'averageSpeed',
            'averageHR',
            'calories',
            'elevationGain',
            'avgWheelchairCadence',
            'maxWheelchairCadence',
            'strokes'
        ],
        [ActivityTypeMapper.TeamSports]: ['distance', 'calories', 'duration', 'averageHR', 'maxHR', 'averageSpeed'],
        [ActivityTypeMapper.RacketSports]: ['distance', 'calories', 'duration', 'averageHR', 'maxHR', 'averageSpeed'],
        [ActivityTypeMapper.Kayaking]: ['distance', 'calories', 'duration', 'averageHR', 'maxHR', 'averageSpeed'],
        [ActivityTypeMapper.Paddling]: ['distance', 'calories', 'duration', 'averageHR', 'maxHR', 'averageSpeed'],
        [ActivityTypeMapper.StandUpPaddleboarding]: [
            'distance',
            'calories',
            'duration',
            'averageHR',
            'maxHR',
            'averageSpeed'
        ],
        [ActivityTypeMapper.Rowing]: ['distance', 'calories', 'duration', 'averageHR', 'maxHR', 'averageSpeed'],
        [ActivityTypeMapper.Shooting]: ['duration', 'averageHR', 'avgStress', 'avgRespirationRate', 'calories']
    },

    // Used in the sorting parameters - The sorting params name must be the alternate value to work... just API stuff
    alternateMetricNames: {
        averageSpeed: 'avgSpeed',
        averageHR: 'avgHr',
        maxHR: 'maxHr',
        averageRunningCadenceInStepsPerMinute: 'avgRunCadence',
        maxRunningCadenceInStepsPerMinute: 'maxRunCadence',
        averageBikingCadenceInRevPerMinute: 'avgBikeCadence',
        maxBikingCadenceInRevPerMinute: 'maxBikeCadence',
        averageSwimCadenceInStrokesPerMinute: 'avgStrokeCadence',
        maxSwimCadenceInStrokesPerMinute: 'maxStrokeCadence',
        averageSwolf: 'avgSwolf',
        leftBalance: 'avgLeftBalance'
    },

    /**
     * Creates a list of activity types based on the format required by the drop down list component.
     * @param types
     * @param {Boolean} [excludeSafetyEvents]
     * @param {Object} [multiSportActivityType]
     * @param {string[]} [bannedActivityTypeKeys] typeKey strings to be excluded
     * @returns {Array}
     */
    formatActivityTypesForDropdownList: function(
        types,
        excludeSafetyEvents,
        multiSportActivityType, // used to add multisport in Backbone Collection. Should fix with React implementation...
        bannedActivityTypeKeys
    ) {
        if (bannedActivityTypeKeys && bannedActivityTypeKeys.length > 0) {
            bannedActivityTypeKeys.forEach((bannedTypeKey) => {
                types = types.filter((type) => type.typeKey !== bannedTypeKey);
            });
        }

        var activityTypes = toParentChildFormat(types);

        if (multiSportActivityType) {
            activityTypes.push(multiSportActivityType);
        }

        var returnList = [];

        activityTypes.forEach((activityType) => {
            var item = {
                nameKey: this.getActivityTypeNameKey(activityType.typeKey),
                value: activityType.typeId,
                typeKey: activityType.typeKey,
                children: [],
                restricted: activityType.restricted,
                displayOrder: getParentTypeDisplayOrderIndex(activityType.typeKey)
            };

            if (activityType.children && activityType.children.length) {
                activityType.children.forEach((activityType) => {
                    item.children.push({
                        nameKey: this.getActivityTypeNameKey(activityType.typeKey),
                        typeKey: activityType.typeKey,
                        value: activityType.typeId,
                        restricted: activityType.restricted
                    });
                });
            }

            if (excludeSafetyEvents) {
                if (!this.isSafetyEvent(item.typeKey)) {
                    returnList.push(item);
                }
            } else {
                returnList.push(item);
            }
        });

        return returnList;
    },

    getActivityTypeNameKey: function(activityTypeKey) {
        return `activity_type_${activityTypeKey}`;
    },

    filterActivityTypesByParent: function(activityTypes, parentTypeArray) {
        return activityTypes.filter((activityType) => {
            return !isUndefined(
                parentTypeArray.find((parentType) => {
                    return activityType.typeKey === parentType;
                })
            );
        });
    },

    filterFormattedActivityTypesByUsage: function(activityTypes, usageIndicators) {
        // filter by usage indicators
        var indicators = usageIndicators.get('performanceBasedIndicators').activityIndicators;
        var filteredActivityTypes = activityTypes.filter((activityType) => {
                if (indicators[activityType.typeKey]) {
                    return indicators[activityType.typeKey].lastActivityDate;
                }
            }),
            activityTypesWhichHaveMetrics = Object.keys(this.activityListColumnConfigDefaults);

        // filter even more by what activity types have metrics defined. This is to guard against displaying filters for activities without metrics.
        filteredActivityTypes = filteredActivityTypes.filter((activityType) => {
            return activityTypesWhichHaveMetrics.indexOf(activityType.typeKey) > -1;
        });

        return filteredActivityTypes;
    },

    /**
     * Searcher the key : value pairs list of alternate metric names and returns the alternate for the given metric name in either direction
     * or undefined if it's not found
     * @param metricName - String
     * @returns {*}
     */
    getAlternateMetricName: function(metricName) {
        if (this.alternateMetricNames.hasOwnProperty(metricName)) {
            return this.alternateMetricNames[metricName];
        } else {
            return Object.keys(this.alternateMetricNames).find((value) =>
                this.alternateMetricNames[value].includes(metricName)
            );
        }
    },

    /**
     * Searches the activity types where the activity types attribute is equal to the given value
     */
    isActivityTypeFound: function(attribute, value, activityTypes, excludeSafetyEvents) {
        var formattedActivityTypes = this.formatActivityTypesForDropdownList(
                activityTypes.toJSON(),
                excludeSafetyEvents
            ),
            match = formattedActivityTypes.filter((obj) => {
                if (obj[attribute] === value) {
                    return obj;
                } else {
                    var a = {};
                    a[attribute] = value;
                    return findWhere(obj.children, a);
                }
            });

        return match.length > 0;
    },

    isDivingActivity: function(typeKey) {
        return (
            typeKey === 'diving' ||
            typeKey === 'multi_gas_diving' ||
            typeKey === 'single_gas_diving' ||
            typeKey === 'apnea_diving' ||
            typeKey === 'pool_apnea' ||
            typeKey === 'gauge_diving' ||
            typeKey === 'apnea_hunting' ||
            typeKey === 'ccr_diving'
        );
    },

    isPoolApnea: function(typeKey) {
        return typeKey === 'pool_apnea';
    },

    isApneaDiving: function(typeKey) {
        return typeKey === 'apnea_diving' || typeKey === 'pool_apnea' || typeKey === 'apnea_hunting';
    },

    isCcrDiving: function(typeKey) {
        return typeKey === 'ccr_diving';
    },

    isGasDiving: function(typeKey) {
        return typeKey === 'multi_gas_diving' || typeKey === 'single_gas_diving' || typeKey === 'ccr_diving';
    },

    isSafetyEvent: function(typeKey) {
        return typeKey === 'safety' || typeKey === 'assistance' || typeKey === 'incident_detected';
    },

    isMultiSportActivity: function(typeKey) {
        return typeKey === 'multi_sport';
    },

    isTransitionActivity: function(typeKey) {
        return (
            typeKey === 'transition' ||
            typeKey === 'transition_v2' ||
            typeKey === 'swimToBikeTransition_v2' ||
            typeKey === 'bikeToRunTransition_v2' ||
            typeKey === 'runToBikeTransition_v2'
        );
    },

    isStrengthTrainingActivity: function(typeKey) {
        return typeKey === 'strength_training';
    },

    isUltraRunActivity: function(typeKey) {
        return typeKey === ActivityTypeMapper.UltraRun;
    },

    isObstacleRunActivity: function(typeKey) {
        return typeKey === ActivityTypeMapper.ObstacleRun;
    },

    isHiitActivity: function(typeKey) {
        return typeKey === ActivityTypeMapper.Hiit;
    },

    isCardioActivity: function(typeKey) {
        return typeKey === ActivityTypeMapper.Cardio;
    },

    isBoxingActivity: function(typeKey) {
        return typeKey === ActivityTypeMapper.Boxing;
    },

    isMixedMartialArtsActivity: function(typeKey) {
        return typeKey === ActivityTypeMapper.MixedMartialArts;
    },

    isPilatesActivity: function(typeKey) {
        return typeKey === ActivityTypeMapper.Pilates;
    },

    isMobilityActivity: function(typeKey) {
        return typeKey === ActivityTypeMapper.Mobility;
    },

    isYogaActivity: function(typeKey) {
        return typeKey === ActivityTypeMapper.Yoga;
    },

    isMindBodyActivity: function(typeKey) {
        return (
            typeKey === ActivityTypeMapper.Yoga ||
            typeKey === ActivityTypeMapper.Pilates ||
            typeKey === ActivityTypeMapper.Mobility
        );
    },

    isBreathworkActivity: function(typeKey) {
        return typeKey === ActivityTypeMapper.Breathwork;
    },

    isMountainBikeActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.MountainBiking;
    },

    isAutoRacingActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.AutoRacing;
    },

    isResortSkiActivity: function(typeKey) {
        return (
            typeKey &&
            (typeKey === ActivityTypeMapper.ResortSkiSnowboard ||
                typeKey === ActivityTypeMapper.ResortSkiing ||
                typeKey === ActivityTypeMapper.ResortSnowboarding)
        );
    },

    isResortSkiingActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.ResortSkiing;
    },

    isResortSnowboardingActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.ResortSnowboarding;
    },

    isBackcountrySkiActivity: function(typeKey) {
        return (
            typeKey &&
            (typeKey === ActivityTypeMapper.BackcountrySki ||
                typeKey === ActivityTypeMapper.BackcountrySkiing ||
                typeKey === ActivityTypeMapper.BackcountrySnowboarding)
        );
    },

    isCrosscountrySkiActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.CrosscountrySki;
    },

    isRepCounting: function(activityTypeKey) {
        var repCountingActivities = [
            'strength_training',
            'indoor_cardio',
            'pilates',
            'yoga',
            'mobility',
            ActivityTypeMapper.Boxing,
            ActivityTypeMapper.MixedMartialArts
        ];

        if (activityTypeKey) {
            return repCountingActivities.indexOf(activityTypeKey) !== -1;
        }

        return false;
    },

    isSkateSkiActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.SkateSki;
    },

    isCardioBasedActivity: function(typeKey) {
        return (
            typeKey &&
            (typeKey === ActivityTypeMapper.Cardio ||
                typeKey === ActivityTypeMapper.Boxing ||
                typeKey === ActivityTypeMapper.MixedMartialArts)
        );
    },

    isNauticalActivity: function(typeKey) {
        return (
            typeKey &&
            (this.isBoating(typeKey) ||
                this.isSailing(typeKey) ||
                this.isOffShoreGrinding(typeKey) ||
                this.isOnShoreGrindingActivity(typeKey) ||
                this.isWindsurfingActivity(typeKey))
        );
    },

    /**
     * In some values, we want to ignore certain Activity Types
     * even though they are Nautical types for some values. For example,
     * we might want Distance to show as Miles instead of Nautical Miles,
     * but for Speed we would want to continue to show Knots
     *
     * @param typeKey: Activity Type key
     */
    isNauticalActivityException: function(typeKey) {
        return typeKey && this.isWindsurfingActivity(typeKey);
    },

    isBoating: function(typeKey) {
        return (
            typeKey && (typeKey === 'boating' || typeKey === ActivityTypeMapper.Boating) //v2
        );
    },

    isSailing: function(typeKey) {
        return (
            typeKey && (typeKey === 'sailing' || typeKey === ActivityTypeMapper.Sailing) //v2
        );
    },

    isOffShoreGrinding: function(typeKey) {
        return (
            typeKey &&
            (typeKey === ActivityTypeMapper.OffShoreGrinding || typeKey === ActivityTypeMapper.OffShoreGrindingV2)
        );
    },

    isOnShoreGrindingActivity: function(typeKey) {
        return (
            typeKey &&
            (typeKey === ActivityTypeMapper.OnShoreGrinding || typeKey === ActivityTypeMapper.OnShoreGrindingV2)
        );
    },

    isIndoorClimbingActivity: function(typeKey) {
        return typeKey === ActivityTypeMapper.IndoorClimbing;
    },

    isFloorClimbingActivity: function(typeKey) {
        return typeKey === ActivityTypeMapper.FloorClimbing;
    },

    isBoulderingActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.Bouldering;
    },

    isTrackRunningActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.TrackRunning;
    },

    isCyclingActivity: function(typeKey) {
        return (
            typeKey &&
            (typeKey === 'cycling' ||
                typeKey === 'mountain_biking' ||
                typeKey === 'road_biking' ||
                typeKey === 'recumbent_cycling' ||
                typeKey === 'track_cycling' ||
                typeKey === 'downhill_biking' ||
                typeKey === 'cyclocross' ||
                typeKey === 'indoor_cycling' ||
                typeKey === 'bmx' ||
                typeKey === 'gravel_cycling' ||
                typeKey === 'e_bike_fitness' ||
                typeKey === 'e_bike_mountain' ||
                typeKey === 'enduro_mtb' ||
                typeKey === 'e_enduro_mtb' ||
                typeKey === 'virtual_ride')
        );
    },

    isEBikeActivity: function(typeKey) {
        return typeKey && (typeKey === 'e_bike_fitness' || typeKey === 'e_bike_mountain');
    },

    hasEBikePerformanceData: function(activity) {
        return !!(
            this.isCyclingActivity(activity.activity.get('activityType').typeKey) &&
            activity.activityDetails &&
            (activity.activityDetails.get('directEbikeAssistLevelPercent') ||
                activity.activityDetails.get('directEbikeAssistMode') ||
                activity.activityDetails.get('directEbikeBatteryLevel'))
        );
    },

    isParasportsActivity: function(typeKey) {
        return (
            typeKey &&
            (typeKey === ActivityTypeMapper.Parasports ||
                typeKey === ActivityTypeMapper.WheelchairPushRun ||
                typeKey === ActivityTypeMapper.WheelchairPushWalk)
        );
    },

    isPushRunActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.WheelchairPushRun;
    },

    isMeditationActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.Meditation;
    },

    hasGradeAdjustedPace: function(activitySummary) {
        return !!activitySummary.avgGradeAdjustedSpeed || activitySummary.avgGradeAdjustedSpeed === 0;
    },

    isSurfingActivity: function(typeKey) {
        return typeKey && (typeKey === ActivityTypeMapper.Surfing || typeKey === ActivityTypeMapper.SurfingV2);
    },

    isWatersportsShowDailySummaryLabel: function(typeKey) {
        return (
            typeKey &&
            (typeKey === ActivityTypeMapper.WaterSports ||
                typeKey === ActivityTypeMapper.Boating ||
                typeKey === ActivityTypeMapper.Fishing ||
                typeKey === ActivityTypeMapper.Kayaking ||
                typeKey === ActivityTypeMapper.Kiteboarding ||
                typeKey === ActivityTypeMapper.OffShoreGrindingV2 ||
                typeKey === ActivityTypeMapper.OnShoreGrindingV2 ||
                typeKey === ActivityTypeMapper.Paddling ||
                typeKey === ActivityTypeMapper.WhitewaterRafting ||
                typeKey === ActivityTypeMapper.Rowing ||
                typeKey === ActivityTypeMapper.Sailing ||
                typeKey === ActivityTypeMapper.StandUpPaddleboarding ||
                typeKey === ActivityTypeMapper.SurfingV2 ||
                typeKey === ActivityTypeMapper.Watertubing ||
                typeKey === ActivityTypeMapper.WakeboardingV2 ||
                typeKey === ActivityTypeMapper.Wakesurfing ||
                typeKey === ActivityTypeMapper.Waterskiing)
        );
    },

    isWakesportsActivity: function(typeKey) {
        return (
            typeKey &&
            (typeKey === ActivityTypeMapper.Wakeboarding ||
                typeKey === ActivityTypeMapper.WakeboardingV2 ||
                typeKey === ActivityTypeMapper.Wakesurfing ||
                typeKey === ActivityTypeMapper.Waterskiing ||
                typeKey === ActivityTypeMapper.Watertubing)
        );
    },

    isTeamSportsActivity: function(typeKey) {
        return (
            typeKey &&
            (typeKey === ActivityTypeMapper.TeamSports ||
                typeKey === ActivityTypeMapper.AmericanFootball ||
                typeKey === ActivityTypeMapper.Baseball ||
                typeKey === ActivityTypeMapper.Basketball ||
                typeKey === ActivityTypeMapper.Cricket ||
                typeKey === ActivityTypeMapper.FieldHockey ||
                typeKey === ActivityTypeMapper.IceHockey ||
                typeKey === ActivityTypeMapper.Lacrosse ||
                typeKey === ActivityTypeMapper.Rugby ||
                typeKey === ActivityTypeMapper.Soccer ||
                typeKey === ActivityTypeMapper.Softball ||
                typeKey === ActivityTypeMapper.UltimateDisc ||
                typeKey === ActivityTypeMapper.Volleyball)
        );
    },

    isRacketSportsActivity: function(typeKey) {
        return (
            typeKey &&
            (typeKey === ActivityTypeMapper.RacketSports ||
                typeKey === ActivityTypeMapper.Badminton ||
                typeKey === ActivityTypeMapper.Padel ||
                typeKey === ActivityTypeMapper.Pickleball ||
                typeKey === ActivityTypeMapper.PlatformTennis ||
                typeKey === ActivityTypeMapper.Racquetball ||
                typeKey === ActivityTypeMapper.Squash ||
                typeKey === ActivityTypeMapper.TableTennis ||
                typeKey === ActivityTypeMapper.TennisV2)
        );
    },

    isEsportActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.Esport;
    },

    isFitnessEquipment: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.FitnessEquip;
    },

    isWalkingActivity: function(typeKey) {
        return (
            typeKey &&
            (typeKey === ActivityTypeMapper.Walking ||
                typeKey === ActivityTypeMapper.CasualWalking ||
                typeKey === ActivityTypeMapper.SpeedWalking)
        );
    },

    isHikingActivity: function(typeKey) {
        return typeKey && (typeKey === ActivityTypeMapper.Hiking || typeKey === ActivityTypeMapper.Rucking);
    },

    isRuckingActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.Rucking;
    },

    isMountaineeringActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.Mountaineering;
    },

    isOtherActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.Other;
    },

    isWindsurfingActivity: function(typeKey) {
        return typeKey && (typeKey === ActivityTypeMapper.Windsurfing || typeKey === ActivityTypeMapper.WindsurfingV2);
    },

    isWindsurfingSpeedProActivity: function(typeKey, splitSummaries) {
        if (this.isWindsurfingActivity(typeKey)) {
            const split = splitSummaries.getSplitSummary(WINDSURF_ACTIVE);
            const splitMode = split && split.get('mode');

            return splitMode && splitMode === 'ADVANCED';
        }

        return false;
    },

    isSnorkelingActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.Snorkeling;
    },

    isLapSwimmingActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.LapSwimming;
    },

    isDownhillBikingActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.DownhillBiking;
    },

    isEnduroActivity: function(typeKey) {
        return typeKey && (typeKey === ActivityTypeMapper.EnduroMtb || typeKey === ActivityTypeMapper.EEnduroMtb);
    },

    isShootingActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.Shooting;
    },

    isArcheryActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.Archery;
    },

    isVirtualActivity: function(typeKey) {
        return typeKey && (typeKey === ActivityTypeMapper.VirtualRide || typeKey === ActivityTypeMapper.VirtualRun);
    },

    // Add Activity Type if the Rounds Table is support by that activity
    isRoundsSupported: function(activityTypeKey) {
        var supportsRoundActivities = [
            'hiit',
            'strength_training',
            'indoor_cardio',
            'pilates',
            'yoga',
            'mobility',
            ActivityTypeMapper.Boxing,
            ActivityTypeMapper.MixedMartialArts
        ];

        if (activityTypeKey) {
            return supportsRoundActivities.indexOf(activityTypeKey) !== -1;
        }

        return false;
    },

    PlaceholderCopies: {
        cycling: 'activity_placehodler_cycling',
        running: 'activity_placholder_running',
        swimming: 'activity_placeholer_swim',
        hiking: 'activity_placeholder_hiking',
        fitness_equipment: 'activity_placeholder_workout',
        default: 'activity_placeholder_default'
    },

    getNotePlaceholderCopy: function(activity, activityTypes) {
        var found = null;

        if (activity) {
            found = this.PlaceholderCopies[activity.typeKey];

            if (!found) {
                //try parent activity type
                var parentType = activityTypes.getParentType(activity.typeKey);

                if (parentType) {
                    found = this.PlaceholderCopies[parentType.get('typeKey')];
                }
            }
        }

        if (found) {
            return found;
        } else {
            return this.PlaceholderCopies.default;
        }
    },

    PACE_CHART_SELECTOR_TYPES: {
        NORMAL: 1,
        LAP_AVG: 2
    },

    getListConfigForActivityType: function(activityListColumnConfig, filterObject, activityType, activityTypes) {
        // check if the filtered type / parent is supported
        // then fall back to the activity/parent for the current activity

        let activityTypeKey = activityType.typeKey;

        if (!this.isApneaDiving(activityTypeKey)) {
            // Apnea is the one exception, we always show the same config (even when viewing all dives)
            if (filterObject && filterObject.activityType) {
                activityTypeKey = filterObject.activityType;
            }

            if (filterObject && filterObject.activitySubType) {
                activityTypeKey = filterObject.activitySubType;
            }
        }

        let listConfig = activityListColumnConfig.findWhere({ activityType: activityTypeKey });

        if (!listConfig) {
            // check parent
            listConfig = activityListColumnConfig.findWhere({
                activityType: activityTypes.get(activityType.parentTypeId).get('typeKey')
            });
        }

        return listConfig;
    },

    getAllDisplayableMetricsForActivityType: function(activityType, activityTypes) {
        const activityTypeKey = this.getActivityTypeForMetricsDisplay(activityType, activityTypes).typeKey; // determine if we should look at the parent
        const displayableMetrics = this.activityListAllDisplayableMetrics[activityTypeKey];
        return displayableMetrics;
    },

    getActivityTypeForMetricsDisplay: function(activityType, activityTypes) {
        // only certain child activity types support metric editing, return the proper type that is to be edited in the metrics menu (the child or the parent)
        const activityTypeKey = activityType.typeKey;

        if (activityTypeKey in this.activityListAllDisplayableMetrics) {
            return activityType;
        } else {
            return activityTypes.get(activityType.parentTypeId).toJSON();
        }
    },

    isObjectsEquivalent(a, b) {
        let aProps = Object.getOwnPropertyNames(a),
            bProps = Object.getOwnPropertyNames(b);

        if (aProps.length != bProps.length) {
            return false;
        }

        for (var i = 0; i < aProps.length; i++) {
            var propName = aProps[i];
            if (a[propName] !== b[propName]) {
                return false;
            }
        }

        return true;
    },

    getSplitSummary(splitSummaries, splitType) {
        return (
            (splitSummaries &&
                splitSummaries.find((splitSummary) => {
                    return splitSummary.splitType === splitType;
                })) ||
            {}
        );
    },

    /**
     * Returns the value of the specified key from the specitied splitSummaries splitTypes within the activtity summary.
     * Note, only the first match between the splitSummaries and the compatible splitTypes array will be returned.
     *
     * @param {*} activitySummary -- the activity summary backbone model that contains the splitSummaries
     * @param {*} compatibleSplitTypes -- an array of compatible split types to be used for searching in the splitSummaries. If multiple split types are in this array and multiple matching types exist in the splitSummaries, then only the first match will be used.
     * @param {*} key -- the key of the value to be returned
     * @returns
     */
    getSplitSummaryValueByTypeAndKey: function(activitySummary, compatibleSplitTypes, key) {
        const splitSummaries = activitySummary && activitySummary.get('splitSummaries');
        const compatibleSplitSummary = splitSummaries.find((split) => {
            return compatibleSplitTypes.indexOf(split.splitType) !== -1;
        });

        return compatibleSplitSummary && compatibleSplitSummary[key];
    },

    isCadenceAvailable(activityTypeKey) {
        const activitiesWithNoCadence = [
            'bouldering',
            'indoor_climbing',
            'pilates',
            'strength_training',
            'yoga',
            'mobility'
        ];
        return (
            !this.isMindBodyActivity(activityTypeKey) &&
            !this.isStrengthTrainingActivity(activityTypeKey) &&
            !(activitiesWithNoCadence.indexOf(activityTypeKey) > -1)
        );
    },

    //If the activity is Pool Swimming and swim time (movingDuration) is available, use it to calculate the avg page, if not, use the total time
    getAvgSwimPace(activityTypeKey, movingDuration, totalDuration) {
        let time;
        if (this.isLapSwimmingActivity(activityTypeKey) && movingDuration) {
            time = movingDuration;
        } else {
            time = totalDuration;
        }
        return time;
    },

    /**
     * This function should only be used to get the summary values of running dynamics fields when a split type needs to be omitted.
     * @param {*} activity - the activity json model
     * @param {*} split - the split type that need to be omitted; defaults to OBSTACLE split type
     * @returns an object with all of the running dynamics field values
     */
    getRunningDynamicsExcludingSplit(activity, split = 'OBSTACLE') {
        const runningSplits = activity.splitSummaries?.filter((summary) => summary.splitType !== split);
        if (!runningSplits) return {};

        const averageRunCadence =
            runningSplits.reduce((total, next) => total + next.averageRunCadence, 0) / runningSplits.length;
        const maxRunCadence = Math.max(...runningSplits.map((o) => o.maxRunCadence));
        const strideLength = runningSplits.reduce((total, next) => total + next.strideLength, 0) / runningSplits.length;
        const verticalRatio =
            runningSplits.reduce((total, next) => total + next.verticalRatio, 0) / runningSplits.length;
        const verticalOscillation =
            runningSplits.reduce((total, next) => total + next.verticalOscillation, 0) / runningSplits.length;
        const groundContactBalanceLeft =
            runningSplits.reduce((total, next) => total + next.groundContactBalanceLeft, 0) / runningSplits.length;
        const groundContactTime =
            runningSplits.reduce((total, next) => total + next.groundContactTime, 0) / runningSplits.length;

        return {
            averageRunCadence,
            maxRunCadence,
            strideLength,
            verticalRatio,
            verticalOscillation,
            groundContactBalanceLeft,
            groundContactTime
        };
    },

    isRunningActivity: function(typeKey) {
        return (
            typeKey &&
            (typeKey === 'running' ||
                typeKey === 'trail_running' ||
                typeKey === 'street_running' ||
                typeKey === 'track_running' ||
                typeKey === 'treadmill_running' ||
                typeKey === 'virtual_run' ||
                typeKey === 'obstacle_run' ||
                typeKey === 'indoor_running' ||
                typeKey === 'ultra_run')
        );
    },

    isSkiingActivity: function(typeKey) {
        return (
            typeKey &&
            [
                ActivityTypeMapper.ResortSkiSnowboard,
                ActivityTypeMapper.ResortSkiing,
                ActivityTypeMapper.BackcountrySki,
                ActivityTypeMapper.BackcountrySkiing,
                ActivityTypeMapper.BackcountrySnowboarding,
                ActivityTypeMapper.CrossCountrySkiing,
                ActivityTypeMapper.SkateSkiing
            ].includes(typeKey)
        );
    },

    isDanceActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.Dance;
    },

    isJumpRopeActivity: function(typeKey) {
        return typeKey && typeKey === ActivityTypeMapper.JumpRope;
    },

    // exceptions when certain activity types need to show fields for other parent types
    // for example use, check defaultRenderBasedOnActivityType() in EditActivityPageView.js
    MANUAL_CREATE_ACTIVITY_TYPES_EXCEPTION: {
        [ActivityTypeMapper.Boxing]: ActivityTypeMapper.FitnessEquip,
        [ActivityTypeMapper.MixedMartialArts]: ActivityTypeMapper.FitnessEquip
    }
};

const PowerColorRanges = {
    color_1: ColorLightuiAccent2, //gray
    color_2: ColorBlueLight2, //blue
    color_3: ColorLimeDark2, //green
    color_4: ColorYellowPrimary, //yellow
    color_5: ColorOrangeLight3, //orange
    color_6: ColorRedDark1, //red
    color_7: ColorPurpleDark2, //purple
    color_8: ColorPurpleDark2, //purple
    color_9: ColorPurpleDark2 //purple
};

const RunningPowerColorRanges = {
    color_1: ColorLightuiAccent2,
    color_2: ColorBlueLight2,
    color_3: ColorLimeDark2,
    color_4: ColorOrangeLight3,
    color_5: ColorRedDark1
};

const SpeedColorRanges = {
    color_1: ColorBlueDark3, //blue
    color_2: ColorBluePrimary, //blue
    color_3: ColorBlueLight2, //blue
    color_4: ColorBlueLight1, //blue
    color_5: ColorLimeDark2, //green
    color_6: ColorLimeDark2, //green
    color_7: ColorYellowPrimary, //yellow
    color_8: ColorOrangeLight3, //orange
    color_9: ColorOrangeDark1, //orange
    color_10: ColorRedDark1 //red
};

const PaceColorRanges = {
    color_1: ColorBlueDark3, //blue
    color_2: ColorBluePrimary, //blue
    color_3: ColorBlueLight2, //blue
    color_4: ColorBlueLight1, //blue
    color_5: ColorLimeDark2, //green
    color_6: ColorLimeDark2, //green
    color_7: ColorYellowPrimary, //yellow
    color_8: ColorOrangeLight3, //orange
    color_9: ColorOrangeDark1, //orange
    color_10: ColorRedDark1 //red
};

const HeartRateColorRanges = {
    color_1: ColorLightuiAccent2, //gray
    color_2: ColorBlueLight2, //blue
    color_3: ColorLimeDark2, //green
    color_4: ColorOrangeLight3, //orange
    color_5: ColorRedDark1 //red
};

const ElevationColorRanges = {
    color_1: ColorBlueDark3, //blue
    color_2: ColorBluePrimary, //blue
    color_3: ColorBlueLight2, //blue
    color_4: ColorBlueLight1, //blue
    color_5: ColorLimeDark2, //green
    color_6: ColorLimeDark2, //green
    color_7: ColorYellowPrimary, //yellow
    color_8: ColorOrangeLight3, //orange
    color_9: ColorOrangeDark1, //orange
    color_10: ColorRedDark1 //red
};

export const GritColorRanges = {
    color_1: Color408, //green
    color_2: Color201, //blue
    color_3: Color700, //purple
    max_1: 0,
    max_2: 20,
    max_3: 40
};

export const FlowColorRanges = {
    color_1: Color408, //green
    color_2: Color412, //yellow
    color_3: Color301, //red
    max_1: 0,
    max_2: 1,
    max_3: 20
};

const AssistModeColorRanges = {
    color_1: ColorLightuiAccent2, //gray
    color_2: ColorBlueLight2, //blue
    color_3: ColorLimeDark2, //green
    color_4: ColorYellowPrimary, //yellow
    color_5: ColorOrangeLight3, //orange
    color_6: ColorRedDark1, //red
    color_7: ColorPurpleDark2 //purple
};

export const HeartRateValueRange = {
    minValue: 30,
    maxValue: 254
};

export const StressValueRange = {
    minValue: 0,
    maxValue: 100
};

export const RespirationRateValueRange = {
    minValue: 1,
    maxValue: 60
};

const DEFAULT_MAX = (newPoints) => {
    let max = 0;
    newPoints.forEach((point) => {
        if (point.alt) {
            if (point.alt > max) {
                max = point.alt;
            }
        }
    });
    return max;
};

const DEFAULT_MIN_MAX = (newPoints) => {
    let min = 99999;
    let max = 0;
    newPoints.forEach((point) => {
        if (point.alt) {
            if (point.alt < min) {
                min = point.alt;
            }
            if (point.alt > max) {
                max = point.alt;
            }
        }
    });
    return {
        min,
        max
    };
};
/**
 *
 * @param {array} points
 * finds the min of points after filtering points that fall 1.5 standard
 * deviations below the average
 * finds the max value after filtering points that are > 2 * average
 */
const STDEV_MIN_MAX = (points) => {
    let min = 99999;
    let max = 0;
    let values = points
        .map((point) => point.alt)
        .filter(
            // Filter out 0 values when calculating the average, standard
            // deviation, minimum, and maximum
            (alt) => !!alt
        );
    let results = standardDeviation(values);

    const filteredValues = values.filter(
        (value) =>
            // filter out low values < average - 1.5 * deviation
            value > results.avg - 1.5 * results.stdev &&
            // filter out high values > 2 * avg
            value < results.avg * 2
    );

    filteredValues.forEach((value) => {
        if (value < min) {
            min = value;
        }
        if (value > max) {
            max = value;
        }
    });

    return {
        min,
        max
    };
};

const getMetricTrackLogPalette = (ColorRanges, totalNumberOfColors) => {
    let palette = {};
    for (let i = 0; i < totalNumberOfColors; i++) {
        let stop = Math.round((1 / totalNumberOfColors) * i * 100) / 100;
        palette[stop] = ColorRanges[`color_${i + 1}`];
        if (stop >= 1) {
            break;
        }
    }
    return palette;
};

const getZonesTracklog = (ColorRanges, zoneJson, maxZone) => {
    const zones = zoneJson.models || zoneJson;

    zones.sort((a, b) => {
        return a.zone - b.zone;
    });
    let palette = {};
    let highZone = Math.min(zones.length - 1, maxZone);
    let lowValue = zones[0].zoneLowBoundary;
    let highValue = zones[highZone].zoneLowBoundary;
    let paletteStops = [];
    if (highValue > lowValue) {
        zones.forEach((obj, index) => {
            if (index <= highZone) {
                let stop = Math.min(Math.max((obj.zoneLowBoundary - lowValue) / (highValue - lowValue), 0), 0.999);
                paletteStops.push(stop);
            }
        });
        paletteStops.sort();
        zones.forEach((obj, index) => {
            if (index <= highZone) {
                palette[paletteStops[index]] = ColorRanges[`color_${obj.zoneNumber}`];
            }
        });
    } else {
        palette[0] = ColorRanges[`color_1`];
    }

    return {
        palette: palette,
        minMax: {
            min: lowValue,
            max: highValue
        }
    };
};

export const MAP_OVERLAY_PALETTES = {
    POWER: {
        legendPalette: (args = {}) => {
            const { isRunning = false } = args;

            let palette = isRunning
                ? {
                      // running activities
                      0.0: RunningPowerColorRanges.color_1,
                      0.25: RunningPowerColorRanges.color_2,
                      0.5: RunningPowerColorRanges.color_3,
                      0.75: RunningPowerColorRanges.color_4,
                      1.0: RunningPowerColorRanges.color_5
                  }
                : {
                      // non-running activities
                      0.0: PowerColorRanges.color_1,
                      0.125: PowerColorRanges.color_2,
                      0.25: PowerColorRanges.color_3,
                      0.375: PowerColorRanges.color_4,
                      0.5: PowerColorRanges.color_5,
                      0.625: PowerColorRanges.color_6,
                      0.75: PowerColorRanges.color_7,
                      0.875: PowerColorRanges.color_8,
                      1.0: PowerColorRanges.color_9
                  };
            return palette;
        },
        getPaletteInfo(points, powerTimeInZones, isBackbone, isRunning) {
            let returnObj;

            if (isRunning) {
                returnObj = {
                    palette: this.legendPalette({ isRunning }),
                    minMax: STDEV_MIN_MAX(points)
                };
            } else {
                if (powerTimeInZones) {
                    let jsonArray = powerTimeInZones;
                    if (isBackbone) {
                        jsonArray = powerTimeInZones.toJSON();
                    }
                    returnObj = getZonesTracklog(PowerColorRanges, jsonArray, 6);
                }
            }

            return returnObj;
        },
        minLabel: 'overlay_less_power_label',
        maxLabel: 'overlay_more_power_label'
    },
    SPEED: {
        legendPalette: (args = ({} = {})) => {
            // for typed splits polylines (e.g. resort ski) we need to show the lift splits data on the tracklog with a different color
            const { isResortSki = false } = args;
            let palette = isResortSki
                ? {
                      0.0: ColorSecondary, // gray
                      0.09: ColorBlueDark3, //blue
                      0.18: ColorBluePrimary, //blue
                      0.27: ColorBlueLight2, //blue
                      0.36: ColorBlueLight1, //blue
                      0.45: ColorLimeDark2, //green
                      0.54: ColorLimeDark2, //green
                      0.63: ColorYellowPrimary, //yellow
                      0.72: ColorOrangeLight3, //orange
                      0.81: ColorOrangeDark1, //orange
                      0.9: ColorRedDark1 //red
                  }
                : getMetricTrackLogPalette(SpeedColorRanges, Object.keys(SpeedColorRanges).length);

            return palette;
        },
        getPaletteInfo(points, powerTimeInZones, isBackbone, isRunning, isResortSki) {
            if (isResortSki) {
                return {
                    palette: this.legendPalette({ isResortSki }),
                    minMax: STDEV_MIN_MAX(points)
                };
            } else {
                return {
                    palette: this.legendPalette({ isResortSki }),
                    minMax: STDEV_MIN_MAX(points)
                };
            }
        },
        minLabel: 'overlay_slower_label',
        maxLabel: 'overlay_faster_label'
    },
    PACE: {
        legendPalette: getMetricTrackLogPalette(PaceColorRanges, Object.keys(PaceColorRanges).length),
        getPaletteInfo(points) {
            return {
                palette: this.legendPalette,
                minMax: STDEV_MIN_MAX(points)
            };
        },
        minLabel: 'overlay_slower_label',
        maxLabel: 'overlay_faster_label'
    },
    GRADE_ADJUSTED_PACE: {
        legendPalette: getMetricTrackLogPalette(PaceColorRanges, Object.keys(PaceColorRanges).length),
        getPaletteInfo(points) {
            return {
                palette: this.legendPalette,
                minMax: STDEV_MIN_MAX(points)
            };
        },
        minLabel: 'overlay_slower_label',
        maxLabel: 'overlay_faster_label'
    },
    HEART_RATE: {
        legendPalette: {
            0.0: HeartRateColorRanges.color_1,
            0.2: HeartRateColorRanges.color_2,
            0.5: HeartRateColorRanges.color_3,
            0.7: HeartRateColorRanges.color_4,
            1: HeartRateColorRanges.color_5
        },
        getPaletteInfo(points, hrTimeInZones, isBackbone, isRunning, isResortSki) {
            let returnObj;
            if (hrTimeInZones) {
                new TimeInZonesUtil(hrTimeInZones.models, TimeInZonesUtil.HrZoneNameMapper, isBackbone);
                let jsonArray = hrTimeInZones;
                if (isBackbone) {
                    jsonArray = hrTimeInZones.toJSON();
                }
                returnObj = getZonesTracklog(HeartRateColorRanges, jsonArray, 6);
            }
            if (isResortSki) {
                returnObj = {
                    ...returnObj,
                    palette: { ...returnObj.palette, 0: ColorSecondary },
                    minMax: STDEV_MIN_MAX(points)
                };
                return returnObj;
            }
            return returnObj;
        },
        minLabel: 'lower_heart_rate',
        maxLabel: 'higher_heart_rate'
    },
    ELEVATION: {
        legendPalette: (args = {}) => {
            // for typed splits polylines (e.g. resort ski) we need to show the lift splits data on the tracklog with a different color
            const { isResortSki = false } = args;
            let palette = isResortSki
                ? {
                      0.0: ColorSecondary, // gray
                      0.09: ColorBlueDark3, //blue
                      0.18: ColorBluePrimary, //blue
                      0.27: ColorBlueLight2, //blue
                      0.36: ColorBlueLight1, //blue
                      0.45: ColorLimeDark2, //green
                      0.54: ColorLimeDark2, //green
                      0.63: ColorYellowPrimary, //yellow
                      0.72: ColorOrangeLight3, //orange
                      0.81: ColorOrangeDark1, //orange
                      0.9: ColorRedDark1 //red
                  }
                : getMetricTrackLogPalette(ElevationColorRanges, Object.keys(ElevationColorRanges).length);

            return palette;
        },
        getPaletteInfo(points, powerTimeInZones, isBackbone, isRunning, isResortSki) {
            if (isResortSki) {
                return {
                    palette: this.legendPalette({ isResortSki }),
                    minMax: DEFAULT_MIN_MAX(points)
                };
            } else {
                return {
                    palette: this.legendPalette({ isResortSki }),
                    minMax: DEFAULT_MIN_MAX(points)
                };
            }
        },

        minLabel: 'overlay_lower_label',
        maxLabel: 'overlay_higher_label'
    },
    GRIT: {
        legendPalette: {
            0.0: GritColorRanges.color_1,
            0.5: GritColorRanges.color_2,
            1: GritColorRanges.color_3
        },
        getPaletteInfo() {
            return {
                palette: this.legendPalette,
                minMax: {
                    // these are the fixed values given to us by the Edge team
                    min: 0,
                    max: 40
                }
            };
        },
        minLabel: 'overlay_easy_label',
        maxLabel: 'overlay_hard_label'
    },
    FLOW: {
        legendPalette: {
            0.0: FlowColorRanges.color_1,
            0.5: FlowColorRanges.color_2,
            1: FlowColorRanges.color_3
        },
        getPaletteInfo() {
            return {
                palette: this.legendPalette,
                minMax: {
                    // these are the fixed values given to us by the Edge team
                    min: 0,
                    max: 20
                }
            };
        },
        minLabel: 'overlay_smooth_label',
        maxLabel: 'overlay_rough_label'
    },
    E_BIKE_ASSIST_MODE: {
        // TODO -- figure out 3-7 levels of assist and dynamic legend color
        legendPalette: {
            0: AssistModeColorRanges.color_1,
            0.142: AssistModeColorRanges.color_2,
            0.284: AssistModeColorRanges.color_3,
            0.426: AssistModeColorRanges.color_4,
            0.568: AssistModeColorRanges.color_5,
            0.71: AssistModeColorRanges.color_6,
            0.852: AssistModeColorRanges.color_7,
            1: AssistModeColorRanges.color_7
        },
        getPaletteInfo(points) {
            return {
                palette: this.legendPalette,
                minMax: {
                    min: 0,
                    max: DEFAULT_MAX(points) // TODO is MAX based on max assist level or number of assist levels?
                }
            };
        },
        minLabel: 'overlay_less_assist',
        maxLabel: 'overlay_more_assist'
    }
};

export const MAP_METRIC_LAYERS = {
    POWER: {
        label: 'label_power',
        labelNS: 'units',
        dataKey: 'directPower',
        summaryDataKey: 'averagePower'
    },
    SPEED: {
        label: 'label_speed',
        labelNS: 'units',
        dataKey: 'directSpeed',
        summaryDataKey: 'averageSpeed'
    },
    PACE: {
        label: 'label_pace',
        labelNS: 'units',
        dataKey: 'directSpeed',
        summaryDataKey: 'averageSpeed'
    },
    GRADE_ADJUSTED_PACE: {
        label: 'grade_adjusted_pace',
        labelNS: 'gap',
        dataKey: 'directGradeAdjustedSpeed',
        summaryDataKey: 'avgGradeAdjustedSpeed'
    },
    HEART_RATE: {
        label: 'label_heart_rate',
        labelNS: 'units',
        dataKey: 'directHeartRate',
        summaryDataKey: 'averageHR'
    },
    ELEVATION: {
        label: 'label_elevation',
        labelNS: 'units',
        dataKey: 'directElevation',
        summaryDataKey: 'maxElevation'
    },
    FLOW: {
        label: 'label_flow',
        labelNS: 'activity',
        dataKey: 'directFlow',
        summaryDataKey: 'avgFlow'
    },
    GRIT: {
        label: 'label_grit',
        labelNS: 'activity',
        dataKey: 'directGrit',
        summaryDataKey: 'grit'
    },
    E_BIKE_ASSIST_MODE: {
        label: 'e_bike_assist_mode',
        labelNS: 'ebike',
        dataKey: 'directEbikeAssistLevelPercent',
        summaryDataKey: 'avgEbikeAssistLevelPercent'
    }
};

export const MAP_METRIC_LAYERS_TYPED_SPLITS = {
    RUN_WALK: {
        label: 'label_run_walk_slash',
        isTypedSplit: true,
        splitTypes: [RWD_RUN, RWD_WALK, RWD_STAND],
        splitTypesOptions: {
            RWD_RUN: {
                value: 0,
                legendLabel: 'label_run_walk_run',
                legendLabelNameSpace: 'run_walk',
                legendColor: ColorBluePrimary
            },
            RWD_WALK: {
                value: 1,
                legendLabel: 'label_run_walk_walk',
                legendLabelNameSpace: 'run_walk',
                legendColor: ColorGreenLight1
            },
            RWD_STAND: {
                value: 2,
                legendLabel: 'label_run_walk_idle',
                legendLabelNameSpace: 'run_walk',
                legendColor: ColorOrangeLight1,
                iconURL: '/images/feature/rwd/idle.svg'
            }
        },
        splitTypesWithIcons: [RWD_STAND], // this split type needs to have an iconURL for the SVG to display
        zoomLevelToRemoveLayer: 13, // the number of the zoom level used in zoomHandler
        zoomHandler: (map, typedSplitMarkersLayer, zoomLevelToRemoveLayer, typedSplitPolylineLayer) => {
            /**
             * this is optional. if provided, can control the layer on the map
             * when a zoom event occurs. Performs a check first if the
             * Makers Layer associated to the Polyline Layer is active on the Map
             */
            const isTypedSplitPolylineLayerActive = map.hasLayer(typedSplitPolylineLayer);
            const currentZoomLevel = map.getZoom();

            if (currentZoomLevel < zoomLevelToRemoveLayer && isTypedSplitPolylineLayerActive) {
                map.removeLayer(typedSplitMarkersLayer);
            } else if (isTypedSplitPolylineLayerActive) {
                map.addLayer(typedSplitMarkersLayer);
            }
        },
        getPaletteInfo() {
            /**
             * These values affect the Leaflet Hotline element. The min and max
             * values should match the values set above in splitTypesOptions.
             * The palette is then used by the Hotline to determine the color, so
             * these values should be divisible in between 0 and 1, and deteremined
             * by how many splits exist. These values and the values in splitTypesOptions
             * can be tweaked if the gradient is too strong between splitTypes
             *
             */
            return {
                palette: {
                    0.0: ColorBluePrimary,
                    0.5: ColorGreenLight1,
                    1.0: ColorOrangeLight1
                },
                minMax: { min: 0, max: 2 }
            };
        }
    },
    OBSTACLE: {
        label: 'course_marker_label_obstacle',
        isTypedSplit: true,
        splitTypes: [OBSTACLE],
        splitTypesOptions: {
            OBSTACLE: {
                value: 0,
                iconURL: '/images/feature/obstacleRacing/obstacle.svg'
            }
        },
        splitTypesWithIcons: [OBSTACLE], // this split type needs to have an iconURL for the SVG to display
        zoomLevelToRemoveLayer: 13, // the number of the zoom level used in zoomHandler
        zoomHandler: (map, typedSplitMarkersLayer, zoomLevelToRemoveLayer, typedSplitPolylineLayer) => {
            /**
             * this is optional. if provided, can control the layer on the map
             * when a zoom event occurs. Performs a check first if the
             * Makers Layer associated to the Polyline Layer is active on the Map
             */
            const isTypedSplitPolylineLayerActive = map.hasLayer(typedSplitPolylineLayer);
            const currentZoomLevel = map.getZoom();

            if (currentZoomLevel < zoomLevelToRemoveLayer && isTypedSplitPolylineLayerActive) {
                map.removeLayer(typedSplitMarkersLayer);
            } else if (isTypedSplitPolylineLayerActive) {
                map.addLayer(typedSplitMarkersLayer);
            }
        },
        getPaletteInfo() {
            /**
             * These values affect the Leaflet Hotline element. The min and max
             * values should match the values set above in splitTypesOptions.
             * The palette is then used by the Hotline to determine the color, so
             * these values should be divisible in between 0 and 1, and deteremined
             * by how many splits exist. These values and the values in splitTypesOptions
             * can be tweaked if the gradient is too strong between splitTypes
             *
             */
            return {
                palette: {
                    0.0: ColorRedDark3
                },
                minMax: { min: 0, max: 2 }
            };
        }
    },
    ASCENT_DESCENT: {
        label: 'label_ascent_slash_descent',
        isTypedSplit: true,
        splitTypes: [ASCENT_SPLIT, DESCENT_SPLIT, TRANSITION],
        splitTypesOptions: {
            ASCENT_SPLIT: {
                value: 0,
                legendLabel: 'label_ascent',
                legendLabelNameSpace: 'units',
                legendColor: ColorRedLight2
            },
            DESCENT_SPLIT: {
                value: 1,
                legendLabel: 'label_descent',
                legendLabelNameSpace: 'units',
                legendColor: ColorBlueLight2
            },
            TRANSITION: {
                value: 2,
                legendLabel: 'label_transition',
                legendLabelNameSpace: 'units',
                legendColor: ColorDarkuiAccent3
            }
        },
        getPaletteInfo() {
            /**
             * These values affect the Leaflet Hotline element. The min and max
             * values should match the values set above in splitTypesOptions.
             * The palette is then used by the Hotline to determine the color, so
             * these values should be divisible in between 0 and 1, and deteremined
             * by how many splits exist. These values and the values in splitTypesOptions
             * can be tweaked if the gradient is too strong between splitTypes
             *
             */
            return {
                palette: {
                    0.0: ColorRedLight2,
                    0.5: ColorBlueLight2,
                    1.0: ColorDarkuiAccent3
                },
                minMax: { min: 0, max: 2 }
            };
        }
    },
    RUN_LIFT: {
        label: 'label_run_slash_lift',
        isTypedSplit: true,
        splitTypes: [SKI_RUN_SPLIT, SKI_LIFT_SPLIT],
        splitTypesOptions: {
            SKI_RUN_SPLIT: {
                value: 1,
                legendLabel: 'label_run',
                legendLabelNameSpace: 'activity',
                legendColor: ColorRedPrimary
            },
            SKI_LIFT_SPLIT: {
                value: 0,
                legendLabel: 'label_lift',
                legendLabelNameSpace: 'activity',
                legendColor: ColorSecondary
            }
        },
        getPaletteInfo() {
            /**
             * These values affect the Leaflet Hotline element. The min and max
             * values should match the values set above in splitTypesOptions.
             * The palette is then used by the Hotline to determine the color, so
             * these values should be divisible in between 0 and 1, and deteremined
             * by how many splits exist. These values and the values in splitTypesOptions
             * can be tweaked if the gradient is too strong between splitTypes
             *
             */
            return {
                palette: {
                    0.0: ColorSecondary,
                    1.0: ColorRedPrimary
                },
                minMax: { min: 0, max: 1 }
            };
        }
    },
    PAVED_UNPAVED: {
        label: 'road_surface',
        isTypedSplit: true,
        splitTypes: [SURFACE_TYPE_PAVED, SURFACE_TYPE_UNPAVED],
        splitTypesOptions: {
            SURFACE_TYPE_PAVED: {
                value: 1,
                legendLabel: 'paved',
                legendLabelNameSpace: 'segment',
                legendColor: ColorRedLight2
            },
            SURFACE_TYPE_UNPAVED: {
                value: 0,
                legendLabel: 'unpaved',
                legendLabelNameSpace: 'surface_type',
                legendColor: ColorRedLight2,
                dashed: true
            },
            bordered: true
        },
        getPaletteInfo() {
            /**
             * These values affect the Leaflet Hotline element. The min and max
             * values should match the values set above in splitTypesOptions.
             * The palette is then used by the Hotline to determine the color, so
             * these values should be divisible in between 0 and 1, and deteremined
             * by how many splits exist. These values and the values in splitTypesOptions
             * can be tweaked if the gradient is too strong between splitTypes
             *
             */
            return {
                palette: {
                    0.0: ColorSecondary,
                    1.0: ColorRedPrimary
                },
                minMax: { min: 0, max: 1 }
            };
        }
    },
    DOWNHILL_RUN_LIFT: {
        label: 'label_run_slash_lift',
        isTypedSplit: true,
        splitTypes: [DOWNHILL_BIKING_RUN_SPLIT, DOWNHILL_BIKING_LIFT_SPLIT],
        splitTypesOptions: {
            DOWNHILL_RUN: {
                value: 1,
                legendLabel: 'label_run',
                legendLabelNameSpace: 'activity',
                legendColor: ColorRedPrimary
            },
            DOWNHILL_LIFT: {
                value: 0,
                legendLabel: 'label_lift',
                legendLabelNameSpace: 'activity',
                legendColor: ColorSecondary
            }
        },
        getPaletteInfo() {
            /**
             * These values affect the Leaflet Hotline element. The min and max
             * values should match the values set above in splitTypesOptions.
             * The palette is then used by the Hotline to determine the color, so
             * these values should be divisible in between 0 and 1, and deteremined
             * by how many splits exist. These values and the values in splitTypesOptions
             * can be tweaked if the gradient is too strong between splitTypes
             *
             */
            return {
                palette: {
                    0.0: ColorSecondary,
                    1.0: ColorRedPrimary
                },
                minMax: { min: 0, max: 1 }
            };
        }
    }
};

export const ELEVATION_MANUAL_SCALE = {
    //number constants in meters used for scaling elevation chart
    EXPANDED_NEGATIVE_SCALE: 15,
    EXPANDED_POSITIVE_SCALE: 75,
    NEGATIVE_SCALE: 30,
    POSITIVE_SCALE: 61
};

export const ALLOWED_METRIC_BASED_TRACKLOG_ACTIVITIES = ['running', 'cycling', 'hiking', 'walking', 'para_sports'];

/**
 * Replaces the ALLOWED_METRIC_BASED_TRACKLOG_CHILD_ACTIVITIES array to allow for Role Keys/Feature Flags to be
 * checked for new features.
 * @param {Object} roleKeys - an object of feature flags for adding hidden splits
 */
export const getAllowedMetricBasedTracklogChildActivities = (roleKeys = null) => {
    // these are the already public splits not hidden behind a feature flag, can reuse this for feature flags later
    let allowedChildActivities = [
        ActivityTypeMapper.Windsurfing,
        ActivityTypeMapper.WindsurfingV2,
        ActivityTypeMapper.DiscGolf,
        ActivityTypeMapper.ResortSkiing,
        ActivityTypeMapper.ResortSkiSnowboard,
        ActivityTypeMapper.ResortSnowboarding,
        ActivityTypeMapper.BackcountrySki,
        ActivityTypeMapper.BackcountrySkiing,
        ActivityTypeMapper.BackcountrySnowboarding
    ];

    return allowedChildActivities;
};

export const showExerciseView = (activityTypeKey) => {
    var activityTypes = [
        ActivityTypeMapper.StrengthTraining,
        ActivityTypeMapper.Cardio,
        ActivityTypeMapper.Pilates,
        ActivityTypeMapper.Mobility,
        ActivityTypeMapper.Yoga,
        ActivityTypeMapper.Hiit,
        ActivityTypeMapper.Boxing,
        ActivityTypeMapper.MixedMartialArts
    ];
    return activityTypes.indexOf(activityTypeKey) !== -1;
};

export const RunWalkTypes = [RWD_RUN, RWD_WALK, RWD_STAND];

/**
 * Returns the data to be displayed on the charts.
 *
 * @param {*} typedSplits
 * @param {*} activityDetails
 * @param {*} activityTypeKey
 * @param {*} xAxisType
 * @returns
 */
export const runWalkChartMapper = (typedSplits, activityDetails, activityTypeKey, xAxisType = 'sumDuration') => {
    let data = []; //[timeIntoActivity, typedSplit.type] or [distanceIntoActivity, typedSplit.type]
    let directTimestamps = activityDetails.get('directTimestamp');

    if (typedSplits) {
        typedSplits.forEach((typedSplit) => {
            if (RunWalkTypes.includes(typedSplit.type)) {
                let startTime = DateParser.parseISOUTC(typedSplit.startTimeGMT).getTime();
                let startTimeIndex = ArrayUtil.findClosest(directTimestamps, startTime);
                let x;
                if (xAxisType === 'sumDuration') {
                    x = activityDetails.get('sumDuration')[startTimeIndex];
                } else {
                    // Distance
                    x = Personalizer.convertDistance(
                        activityDetails.get('sumDistance')[startTimeIndex],
                        activityTypeKey
                    );
                }
                data.push({
                    x,
                    y: typedSplit.type
                });
            }
        });
    }

    return data;
};

/**
 * Gets the data to populate the polyline on the activity details map
 *
 * @param {*} typedSplits
 * @param {*} activityDetails
 * @returns
 */
export const runWalkPolylineData = (typedSplits, activityDetails) => {
    let points = [];
    let lngs = activityDetails.get('directLongitude');
    let lats = activityDetails.get('directLatitude');
    let timeStamps = activityDetails.get('directTimestamp');

    let typedSplitsTimestamps = typedSplits.map((typedSplit) => {
        if (RunWalkTypes.includes(typedSplit.type)) {
            return DateParser.parseISOUTC(typedSplit.startTimeGMT).getTime();
        } else {
            return 0;
        }
    });

    for (let i = 0; i < timeStamps.length; i++) {
        let timeStamp = timeStamps[i];
        let index = ArrayUtil.findClosest(typedSplitsTimestamps, timeStamp);
        if (index > -1) {
            let lat = lats[i];
            let lng = lngs[i];
            let zValue = typedSplits[index].type;
            if (lat !== null && lng !== null) {
                points.push(new Garmin.Map.Point(lat, lng, undefined, zValue));
            }
        }
    }

    return points;
};

export function ensureActivityTypesOrder(activityTypes) {
    activityTypes.sort((a, b) => {
        const indexA = parentTypeDisplayOrder.indexOf(a.typeKey);
        const indexB = parentTypeDisplayOrder.indexOf(b.typeKey);
        return indexA - indexB;
    });
}
